import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { CONTACT } from "@/data/site";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about-us-luxury-spa", label: "About Us", id: "about" },
  { to: "/services-luxury-spa", label: "Services", id: "services" },
  { to: "/membership-luxury-spa", label: "Membership", id: "membership" },
  { to: "/contact-us-luxury-spa", label: "Contact Us", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const scrollTopIfSame = (to) => {
    if (pathname === to) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open ? "glass-dark border-b border-gold/15" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="Belong Saloon and Spa home"
          onClick={() => scrollTopIfSame("/")}
        >
          <Logo compact={scrolled} />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.id}`}
              onClick={() => scrollTopIfSame(item.to)}
              className={({ isActive }) =>
                `relative text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-stone-300 hover:text-cream"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-500 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href={CONTACT.phoneTel}
            data-testid="nav-call-button"
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm font-semibold text-gold transition-all duration-300 hover:bg-gold hover:text-stone-950"
          >
            <Phone size={14} />
            {CONTACT.phoneDisplay}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          className="rounded-full border border-gold/30 p-2.5 text-cream lg:hidden"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold/10 lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-${item.id}`}
                    onClick={() => scrollTopIfSame(item.to)}
                    className={({ isActive }) =>
                      `block py-2.5 font-serif-display text-2xl ${isActive ? "text-gold" : "text-stone-200"}`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={CONTACT.phoneTel}
                data-testid="mobile-nav-call"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-stone-950"
              >
                <Phone size={15} /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
